import React from 'react';
import { TableCell } from '@/components/ui/table';

/**
 * The unique-URL cell of the bot crawl table. A bot's count may be a HyperLogLog ESTIMATE
 * (`unique_urls_estimate`, with `unique_urls_rse`) or an exact count that hit the capture agent's
 * cap (`unique_urls_saturated`); older rows carry neither (task-sl8e2nlcpo, paired with the
 * backend fold that carries the flags per date and per window). The header names the bound of the
 * unflagged population, so a flagged VALUE must say what it is, or an estimate reads as exact.
 */
export interface BotUniqueUrlsFlags {
  bot_name: string;
  requests: number;
  bytes: number;
  unique_urls: number;
  unique_urls_estimate?: boolean;
  unique_urls_rse?: number | null;
  unique_urls_saturated?: boolean;
  // 'day': the row carries the persisted day sketch; 'run': that sketch could not be loaded and the
  // row is one run's estimate, a lower bound on the day (task-sl8e2nlcpo ruling (i))
  unique_urls_scope?: 'day' | 'run' | null;
  // window breakdown only: the parts of the window sum from capped days and from estimated days
  unique_urls_capped_part?: number;
  unique_urls_estimated_part?: number;
  // window breakdown only: the part from legacy chunk-peak days, and whether every estimated day
  // reported its error (false: the rse shown covers only the days that did)
  unique_urls_peak_part?: number;
  unique_urls_rse_known?: boolean;
  // window breakdown only: the FULL sketch estimates the rse belongs to; the absolute error is
  // basis × rse, never estimated_part × rse (a relative error does not shrink with a subtraction)
  unique_urls_estimate_basis?: number;
}

function rseText(bot: BotUniqueUrlsFlags): { pct: string | null; err: string } {
  const unknown = bot.unique_urls_rse_known === false || bot.unique_urls_rse == null;
  if (unknown) return { pct: null, err: '; the error was not reported for every value' };
  const pct = `${(bot.unique_urls_rse! * 100).toFixed(2)}%`;
  return { pct, err: `, relative standard error ${pct}` };
}

export function describeBotUniqueUrls(bot: BotUniqueUrlsFlags): { text: string; title?: string } {
  const n = bot.unique_urls.toLocaleString('en-US');
  const capped = bot.unique_urls_capped_part || 0;
  const peak = bot.unique_urls_peak_part || 0;
  const partEstimated = bot.unique_urls_estimate && (bot.unique_urls_saturated || capped > 0 || peak > 0);
  if (partEstimated) {
    // the figure sums days of different kinds (both readers OR the flags across dates): a capped
    // day (a floor), a legacy chunk-peak day (a lower bound) and sketch days (estimates). It is
    // its own state (codex rounds 1 and 2 on #5941). The certain part is the non-estimated part;
    // the sum is NOT a guaranteed floor because the estimated part can overshoot, so the mark is
    // "about, at least the certain part", and the error applies to the estimated part only.
    const { pct, err } = rseText(bot);
    const est = bot.unique_urls_estimated_part || 0;
    const fmt = (v: number) => v.toLocaleString('en-US');
    const pieces: string[] = [];
    if (capped > 0 || (bot.unique_urls_saturated && est === 0 && peak === 0)) {
      pieces.push(`${capped > 0 ? fmt(capped) + ' ' : 'part '}from days where the capture agent's URL cap was hit (a floor)`);
    }
    if (peak > 0) pieces.push(`${fmt(peak)} from legacy days with the largest chunk's count (chunk peaks, a lower bound)`);
    // the error belongs to the FULL sketch estimate, stated as an absolute figure on that basis;
    // the estimated part is the excess above the certain floor and carries no percentage of its own
    const basis = bot.unique_urls_estimate_basis || 0;
    if (pct && basis > 0) {
      const abs = Math.round(basis * (bot.unique_urls_rse as number));
      pieces.push(`the rest from a HyperLogLog sketch estimate of ${fmt(basis)} ±${pct} (about ±${fmt(abs)} URLs), of which ${fmt(est)} lies above the certain floor`);
    } else {
      pieces.push(`${est > 0 ? fmt(est) + ' ' : 'part '}from days with a HyperLogLog estimate${pct ? ` ±${pct}` : ''}`);
    }
    const certain = capped + peak;
    // a run-scoped sketch means at least one day carries one run's estimate instead of the day's
    // (its persisted sketch could not be loaded); that missing coverage is not in the rse and must
    // stay visible in the mixed state too (codex round 7)
    const lostSketch = bot.unique_urls_scope === 'run'
      ? " On at least one day the persisted day sketch could not be loaded and that day carries one run's estimate "
        + 'instead of the day\'s, so part of the day is missing; that missing coverage is not covered by the error figure.'
      : '';
    return {
      text: `≳ ${n} (part estimated)`,
      title: `About ${n}, the sum of daily values over the selected window, part estimated: ${pieces.join('; ')}${pct ? '' : err}. `
        + `At least ${fmt(certain)} is certain (the non-estimated part); the estimated part can be above or below its figure, `
        + 'and the error applies to the estimated part only.' + lostSketch,
    };
  }
  if (bot.unique_urls_estimate) {
    const { err } = rseText(bot);
    // this cell shows the window breakdown: a SUM of daily values over the selected window, never
    // one day's or one run's count (codex round 5)
    if (bot.unique_urls_scope === 'run') {
      return {
        text: `≳ ${n}`,
        title: `Sum over the selected window of each day's distinct-URL estimate (HyperLogLog sketch)${err}, where on `
          + "at least one day the persisted day sketch could not be loaded and that day carries one run's estimate "
          + 'instead: an estimate of a lower bound, not a guaranteed one.',
      };
    }
    if (bot.unique_urls_scope === 'day') {
      return {
        text: `≈ ${n}`,
        title: `Sum over the selected window of each day's distinct-URL estimate (HyperLogLog sketch persisted across `
          + `the day's runs)${err}. Not an exact count, and not a distinct count across days.`,
      };
    }
    return {
      text: `≈ ${n}`,
      title: `Sum over the selected window of daily estimates (HyperLogLog sketch)${err}. Not an exact count.`,
    };
  }
  if (bot.unique_urls_saturated) {
    return {
      text: `≥ ${n}`,
      title: "The capture agent's per-bot URL cap was hit: the true count is at least this many.",
    };
  }
  return { text: n };
}

export function BotUniqueUrlsCell({ bot }: { bot: BotUniqueUrlsFlags }) {
  const d = describeBotUniqueUrls(bot);
  return <TableCell className="text-right" title={d.title}>{d.text}</TableCell>;
}
